import { RefObject, useCallback, useEffect, useRef } from 'react'

import { useDebouncedCallback } from 'use-debounce'

const SCROLL_DEBOUNCE = 100
const SCROLL_OFFSET = 20

interface UseInfiniteScrollContainerParams {
  isLoading?: boolean
  hasMore?: boolean
  onNextPage: () => void
  scrollTopOffsetRef: RefObject<number>
}

export const useInfiniteScrollContainer = ({
  isLoading,
  scrollTopOffsetRef,
  hasMore,
  onNextPage,
}: UseInfiniteScrollContainerParams) => {
  const containerRef = useRef<HTMLDivElement>(null)

  const handleScroll = useCallback(() => {
    if (containerRef.current) {
      scrollTopOffsetRef.current = containerRef.current.scrollTop

      const isAtEnd =
        containerRef.current.scrollHeight - containerRef.current.scrollTop <=
        containerRef.current.clientHeight + SCROLL_OFFSET

      // store scroll position and load next page
      if (isAtEnd && hasMore && !isLoading) {
        onNextPage()
      }
    }
  }, [hasMore, isLoading, onNextPage, scrollTopOffsetRef])

  const debouncedScroll = useDebouncedCallback(handleScroll, SCROLL_DEBOUNCE)

  // keep scroll position on rerender
  useEffect(() => {
    if (containerRef.current && scrollTopOffsetRef.current) {
      containerRef.current.scrollTop = scrollTopOffsetRef.current
    }
  }, [scrollTopOffsetRef])

  return { onScroll: debouncedScroll, containerRef }
}
